import {
  resolvePixelConfig,
  type PixelConfig,
  type PixelSettings,
} from '@/config';
import type { PixelEvent } from '@/events';
import type {
  AddPaymentInfoData,
  AddToCartData,
  InitiateCheckoutData,
  LeadData,
  PurchaseData,
  RemoveFromCartData,
  SearchData,
  ViewContentData,
} from '@/events/conversion';
import type { PixelRuntimeServices } from '@/services/types';
import {
  DebugSnapshotService,
  RuntimeDiagnosticsModule,
  type PixelDebugSnapshot,
} from '@/diagnostics';
import {
  BehaviorModule,
  ConversionModule,
  CoreModule,
  IntegrationsModule,
  PrivacyModule,
  TransportModule,
} from '@/modules';
import { PixelRuntime } from '@/runtime';
import { SDK_VERSION } from '@/version';

export class Pixel {
  private readonly runtime: PixelRuntime;

  private readonly config: PixelConfig;

  private readonly conversionModule: ConversionModule;

  private readonly transportModule: TransportModule;

  private readonly integrationsModule: IntegrationsModule;

  private readonly behaviorModule: BehaviorModule;

  private readonly snapshotService: DebugSnapshotService;

  private readonly diagnosticsModule: RuntimeDiagnosticsModule;

  constructor(settings: PixelSettings) {
    const config = resolvePixelConfig(settings);
    this.config = config;
    this.runtime = new PixelRuntime(config);
    this.conversionModule = new ConversionModule();
    this.transportModule = new TransportModule();
    this.integrationsModule = new IntegrationsModule();
    this.behaviorModule = new BehaviorModule();

    this.snapshotService = new DebugSnapshotService({
      getSdkVersion: () => SDK_VERSION,
      getRuntime: () => this.runtime,
      getTransportModule: () => this.transportModule,
      getConversionModule: () => this.conversionModule,
      getIntegrationsModule: () => this.integrationsModule,
      getBehaviorModule: () => this.behaviorModule,
      getConfig: () => this.config,
      getRegisteredModules: () => [
        {
          name: 'privacy',
          configuredEnabled: config.modules.privacy.enabled === true,
        },
        {
          name: 'transport',
          configuredEnabled: config.transport.enabled === true,
        },
        {
          name: 'integrations',
          configuredEnabled: config.modules.integrations.enabled === true,
        },
        {
          name: 'core',
          configuredEnabled: config.modules.core.enabled === true,
        },
        {
          name: 'behavior',
          configuredEnabled: config.modules.behavior.enabled === true,
        },
        {
          name: 'conversion',
          configuredEnabled: config.modules.conversion.enabled === true,
        },
        { name: 'diagnostics', configuredEnabled: true },
      ],
    });
    this.diagnosticsModule = new RuntimeDiagnosticsModule(this.snapshotService);

    // Ordem crítica: Transport + Integrations assinam Dispatcher
    // ANTES do Core emitir PAGE_VIEW.
    this.runtime.register(new PrivacyModule());
    this.runtime.register(this.transportModule);
    this.runtime.register(this.integrationsModule);
    this.runtime.register(new CoreModule());
    this.runtime.register(this.behaviorModule);
    this.runtime.register(this.conversionModule);
    this.runtime.register(this.diagnosticsModule);
  }

  init(): Promise<void> {
    this.snapshotService.markAlive();
    return this.runtime.init();
  }

  destroy(): Promise<void> {
    return this.runtime.destroy().then(() => {
      this.snapshotService.markDestroyed();
    });
  }

  isInitialized(): boolean {
    return this.runtime.isInitialized();
  }

  /**
   * Snapshot oficial read-only do Runtime (Etapa 16).
   * Seguro antes/depois do init e após destroy. Retorno congelado e sanitizado.
   */
  getDebugSnapshot(): Readonly<PixelDebugSnapshot> {
    return this.snapshotService.getSnapshot();
  }

  /**
   * Define ou substitui um managed param do UrlContext após o init
   * (ex.: `oid` quando a Order é criada no checkout).
   * No-op se UrlContext desabilitado ou o param não estiver na config.
   */
  setManagedUrlParam(param: string, value: string): boolean {
    const services = this.runtime.getDebugServices();
    if (!services?.urlContext) {
      return false;
    }
    return services.urlContext.setManagedParam(param, value);
  }

  purchase(data?: PurchaseData): void {
    this.conversionModule.purchase(data);
  }

  lead(data: LeadData): void {
    this.conversionModule.lead(data);
  }

  addToCart(data?: AddToCartData): void {
    this.conversionModule.addToCart(data);
  }

  removeFromCart(data?: RemoveFromCartData): void {
    this.conversionModule.removeFromCart(data);
  }

  initiateCheckout(data?: InitiateCheckoutData): void {
    this.conversionModule.initiateCheckout(data);
  }

  addPaymentInfo(data?: AddPaymentInfoData): void {
    this.conversionModule.addPaymentInfo(data);
  }

  viewContent(data?: ViewContentData): void {
    this.conversionModule.viewContent(data);
  }

  search(data: SearchData): void {
    this.conversionModule.search(data);
  }

  /** @internal For playground and development only. */
  getDebugDedupeKeys(): readonly string[] {
    return this.conversionModule.getDebugDedupeKeys();
  }

  /** @internal For playground and development only. */
  getDebugActiveDetectors(): readonly string[] {
    return this.conversionModule.getDebugActiveDetectors();
  }

  /** @internal For playground and development only. */
  getDebugTransportState() {
    return this.transportModule.getDebugState();
  }

  /** @internal For playground — flush manual do transport. */
  flushTransport(): void {
    this.transportModule.manualFlush?.();
  }

  /** @internal For playground — simular offline. */
  simulateTransportOffline(offline: boolean): void {
    this.transportModule.simulateOffline?.(offline);
  }

  /** @internal For playground — configure the TestSender. */
  configureTransportTestSender(options: {
    capture?: boolean;
    response?: 'success' | 'failure';
    delayMs?: number;
    failUntilAttempt?: number;
    simulateTimeout?: boolean;
  }): void {
    this.transportModule.configureTestSender?.(options);
  }

  /** @internal For playground — simular reload da página. */
  simulateTransportReload(): Promise<void> {
    return this.transportModule.simulateReload();
  }

  /** @internal For playground — simular restart do transport. */
  simulateTransportRestart(): Promise<void> {
    return this.transportModule.simulateRestart();
  }

  /** @internal For playground — abrir circuit breaker. */
  forceTransportCircuitOpen(): void {
    this.transportModule.forceCircuitOpen();
  }

  /** @internal For playground — fechar circuit breaker. */
  forceTransportCircuitClose(): void {
    this.transportModule.forceCircuitClose();
  }

  /** @internal For playground — replay manual da fila persistida. */
  manualTransportReplay(): Promise<number> {
    return this.transportModule.manualReplay();
  }

  /** @internal For playground — replay dead letter. */
  replayTransportDeadLetter(): PixelEvent[] {
    return this.transportModule.replayDeadLetter();
  }

  /** @internal For playground — limpar dead letter. */
  clearTransportDeadLetter(): void {
    this.transportModule.clearDeadLetter();
  }

  /** @internal For playground — exportar fila. */
  exportTransportQueue(): string {
    return this.transportModule.exportQueue();
  }

  /** @internal For playground — importar fila. */
  importTransportQueue(raw: string): number {
    return this.transportModule.importQueue(raw);
  }

  /** @internal For playground — Integration Engine debug state. */
  getDebugIntegrationState() {
    return this.integrationsModule.getDebugState();
  }

  /** @internal For playground — MemoryDestination records. */
  getDebugIntegrationMemoryRecords() {
    return this.integrationsModule.getMemoryRecords();
  }

  /** @internal For playground — Attribution snapshot. */
  getDebugAttributionSnapshot() {
    return this.runtime.getDebugServices()?.attribution.getSnapshot() ?? null;
  }

  /** @internal For playground — recolher attribution. */
  collectAttribution() {
    return this.runtime.getDebugServices()?.attribution.collect() ?? null;
  }

  /** @internal For playground — limpar attribution. */
  clearAttribution(): void {
    this.runtime.getDebugServices()?.attribution.clear();
  }

  /** @internal For playground and development only. Not part of the stable public API. */
  getDebugServices(): PixelRuntimeServices | null {
    return this.runtime.getDebugServices();
  }

  /** @internal For playground — força avaliação de engagement. */
  evaluateEngagement(): void {
    this.behaviorModule.evaluateEngagementNow();
  }

  /** @internal For playground — publica sinal interno de comportamento. */
  debugPublishBehaviorSignal(
    type: Parameters<BehaviorModule['debugPublishSignal']>[0],
    data?: Parameters<BehaviorModule['debugPublishSignal']>[1]
  ): void {
    this.behaviorModule.debugPublishSignal(type, data);
  }

  /** @internal For playground — flush manual do behavior buffer. */
  flushBehaviorBuffer(): void {
    this.behaviorModule.flushBufferNow();
  }

  /** @internal For playground — emite BEHAVIOR_SESSION_SUMMARY manualmente. */
  emitBehaviorSessionSummary(
    reason?: Parameters<BehaviorModule['emitSessionSummaryNow']>[0]
  ): boolean {
    return this.behaviorModule.emitSessionSummaryNow(reason);
  }
}

export type { PixelDebugSnapshot };
